import { FC } from 'react';
import {
  FileInput,
  InfoCard,
  Modal,
  SelectInput,
  SliderInput,
  SwitchInput,
  TextInput,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps, useModalContext } from 'store/modal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { Stack } from '@mui/material';
import { AutoStoriesOutlined, CommentOutlined } from '@mui/icons-material';
import { binaries, Binary } from 'types/Common';
import { useCompanyLanguages } from 'state/Administrators';
import { useUploadFile } from 'store/files';
import {
  trainingDifficulties,
  TrainingDifficulty,
  surveyChoiceAnswerTypes,
  SurveyChoiceAnswerType,
  trainingTones,
  TrainingTone,
  MAX_QUESTIONS_FOR_AI_GENERATION,
  useGenerateTrainingFromPrompt,
} from 'store/surveys';
import { useJobsContext } from 'store/jobs';
import { JobStatusModal } from 'components/modals';
import { GenerateTrainingModal } from 'containers/surveys';
import { useTrainingGenerationJobStatusProps } from '../training-generation-progress-modal/use-training-generation-job-status-props';

interface Values {
  name: string;
  files: File[];
  instructions: string;
  showOptions: boolean;
  questionsNumber: number;
  questionType: SurveyChoiceAnswerType[];
  difficulty: TrainingDifficulty;
  tone: TrainingTone;
  contentPages: Binary;
}

const INITIAL_VALUES: Values = {
  name: '',
  files: [],
  instructions: '',
  showOptions: false,
  questionsNumber: 5,
  questionType: [...surveyChoiceAnswerTypes],
  difficulty: 'medium',
  tone: 'friendly',
  contentPages: 'no',
};

interface Props extends ModalProps {
  type: 'material' | 'prompt';
}

export const GenerateTrainingFromPromptModal: FC<Props> = ({
  closeModal,
  type,
}) => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();
  const { addInProgressJob } = useJobsContext();
  const trainingJobStatusProps = useTrainingGenerationJobStatusProps();
  const { defaultLanguage } = useCompanyLanguages();

  const { mutateAsync: uploadFile, isLoading: isUploading } = useUploadFile();
  const { mutate: generateTraining, isLoading: isGenerating } =
    useGenerateTrainingFromPrompt();

  const card =
    type === 'material'
      ? {
          icon: AutoStoriesOutlined,
          title: t('trainings.generate.material.title'),
          text: t('trainings.generate.material.description'),
        }
      : {
          icon: CommentOutlined,
          title: t('trainings.generate.prompt.title'),
          text: t('trainings.generate.prompt.description'),
        };

  const { control, handleSubmit, watch } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const [showOptions] = watch(['showOptions']);

  const onGenerate: SubmitHandler<Values> = async (values) => {
    const fileIds =
      type === 'material'
        ? await Promise.all(
            values.files.map(async (file) => {
              const { data } = await uploadFile({ file });
              return data.id;
            }),
          )
        : undefined;

    generateTraining(
      {
        fileIds,
        name: values.name,
        language: defaultLanguage,
        instructions: values.instructions,
        questionsNumber: values.questionsNumber,
        questionType: values.questionType,
        difficulty: values.difficulty,
        tone: values.tone,
        contentPages: values.contentPages === 'yes',
      },
      {
        onSuccess: ({ data }) => {
          addInProgressJob(data.jobId);
          closeModal();
          openModal({
            component: JobStatusModal,
            props: trainingJobStatusProps(data.jobId),
            closable: false,
          });
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onGenerate)}>
      <Modal.Content
        title={t('components.file_input.generate_ai')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('ai.actions.generate'),
            type: 'submit',
            disabled: isUploading || isGenerating,
          },
        ]}
      >
        <Stack gap={3}>
          <InfoCard
            icon={card.icon}
            title={card.title}
            text={card.text}
            variant="info"
            direction="row"
            action={{
              label: t('common.back'),
              onClick: () => {
                closeModal();
                openModal({ component: GenerateTrainingModal, size: 'xl' });
              },
            }}
          />

          <Controller
            name="name"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextInput
                label={t('alternatives.systemName')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          {type === 'material' ? (
            <Controller
              name="files"
              control={control}
              render={({ field: { name, onChange } }) => (
                <FileInput
                  name={name}
                  variant="compact"
                  maxFiles={10}
                  onChange={onChange}
                  accept={['pdf', 'doc', 'docx', 'ppt', 'pptx']}
                  required
                />
              )}
            />
          ) : null}

          <Controller
            name="instructions"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextInput
                label={
                  type === 'material'
                    ? t('trainings.generate.instructions.label')
                    : t('ai.prompt.label')
                }
                rows={3}
                name={name}
                value={value}
                onChange={onChange}
                infoMessage={
                  value.length && value.length < 50
                    ? t('trainings.generate.fileIds.error.min')
                    : undefined
                }
                required={type === 'prompt'}
                ref={ref}
              />
            )}
          />

          <Stack gap={2}>
            <Controller
              name="showOptions"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <SwitchInput
                  label={t('ai.options.label')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  ref={ref}
                />
              )}
            />

            {showOptions ? (
              <>
                <Controller
                  name="questionsNumber"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <SliderInput
                      label={t('trainings.generate.questionsNumber.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      min={1}
                      max={MAX_QUESTIONS_FOR_AI_GENERATION}
                      step={1}
                      ref={ref}
                    />
                  )}
                />

                <Stack
                  gap={1}
                  display="grid"
                  gridTemplateColumns="repeat(2, 1fr)"
                >
                  <Controller
                    name="questionType"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => (
                      <SelectInput
                        label={t('trainings.generate.questionType.label')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={surveyChoiceAnswerTypes.map(
                          (questionType) => ({
                            value: questionType,
                            label: t(`surveys.answerType-${questionType}`),
                          }),
                        )}
                        multiple
                        required
                        ref={ref}
                      />
                    )}
                  />

                  <Controller
                    name="difficulty"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => (
                      <SelectInput
                        label={t('trainings.generate.difficulty.label')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={trainingDifficulties.map((difficulty) => ({
                          value: difficulty,
                          label: t(
                            `trainings.generate.difficulty.${difficulty}`,
                          ),
                        }))}
                        ref={ref}
                      />
                    )}
                  />

                  <Controller
                    name="tone"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => (
                      <SelectInput
                        label={t('trainings.generate.tone.label')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={trainingTones.map((tone) => ({
                          value: tone,
                          label: t(`trainings.generate.tone.${tone}`),
                        }))}
                        ref={ref}
                      />
                    )}
                  />

                  <Controller
                    name="contentPages"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => (
                      <SelectInput
                        label={t('trainings.generate.contentPages.label')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={binaries.map((binary) => ({
                          value: binary,
                          label: t(`common.${binary}`),
                        }))}
                        ref={ref}
                      />
                    )}
                  />
                </Stack>
              </>
            ) : null}
          </Stack>
        </Stack>
      </Modal.Content>
    </form>
  );
};
